import React from "react";
import { advantages as advIcons, teamMoments as tmImages } from "../../data/mock";
import { ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { useLanguage } from "../../i18n/LanguageContext";

export default function WhyUs() {
  const { t } = useLanguage();
  const advTexts = t("whyUs.advantages") || [];
  const teamTexts = t("whyUs.teamMoments") || [];

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="why-us" className="relative py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 hero-bg" />
      <div className="absolute inset-0 stars" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="kicker">{t("whyUs.kicker")}</span>
          <h2 className="mt-5 font-display text-4xl lg:text-5xl font-bold text-white leading-tight">
            {t("whyUs.title1")} <span className="grad-text">{t("whyUs.titleAccent")}</span>{" "}{t("whyUs.titleTail")}
          </h2>
          <p className="mt-4 text-slate-300/90 text-lg">{t("whyUs.description")}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-20">
          {advIcons.map((a, i) => {
            const Icon = a.icon;
            const text = advTexts[i] || { title: a.title, desc: a.desc };
            return (
              <div key={text.title} className="glass lift rounded-2xl p-8">
                <div className="flex items-start gap-5">
                  <div className="shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-400 shadow-lg shadow-cyan-500/25 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-white" strokeWidth={2.2} />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-white">{text.title}</h3>
                    <p className="mt-2 text-slate-400 leading-relaxed">{text.desc}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="kicker">{t("whyUs.insideKicker")}</span>
          <h3 className="mt-5 font-display text-3xl lg:text-4xl font-bold text-white">
            {t("whyUs.insideTitle1")} <span className="grad-text">{t("whyUs.insideTitleAccent")}</span>
          </h3>
          <p className="mt-4 text-slate-300/90">{t("whyUs.insideDesc")}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {tmImages.map((m, i) => {
            const text = teamTexts[i] || { title: m.title, subtitle: m.subtitle };
            return (
              <div key={i} className="group relative rounded-2xl overflow-hidden glass lift">
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={m.image} alt={text.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition duration-700" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#050818] via-[#050818]/50 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <h4 className="font-display text-lg font-semibold text-white">{text.title}</h4>
                  <p className="mt-1 text-sm text-slate-300/90">{text.subtitle}</p>
                </div>
                <div className="absolute top-4 right-4 px-2.5 py-1 text-[10px] tracking-widest uppercase font-semibold rounded-full bg-cyan-500/15 border border-cyan-400/30 text-cyan-200">
                  {String(i + 1).padStart(2, "0")}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-20 relative rounded-3xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-cyan-500/20 to-blue-600/20" />
          <div className="absolute inset-0 dotted opacity-40" />
          <div className="relative glass rounded-3xl px-8 lg:px-14 py-14 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl">
              <h3 className="font-display text-3xl lg:text-4xl font-bold text-white leading-tight">
                {t("whyUs.ctaTitle1")} <span className="grad-text">{t("whyUs.ctaTitleAccent")}</span>
              </h3>
              <p className="mt-4 text-slate-300">{t("whyUs.ctaDesc")}</p>
            </div>
            <Button onClick={() => scrollTo("contact")} className="btn-primary h-12 px-7 rounded-xl font-semibold text-base border-0 whitespace-nowrap">
              {t("whyUs.ctaButton")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
